import { Formik, FormikProps } from 'formik'
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AddSingleMCPaymentFormValues } from './AddSingleMCPaymentForm.interface'
import { AddSingleMCPaymentSchema } from './AddSingleMCPaymentForm.validator'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { handleApiError } from 'src/utils/errorHandlers'
import { FormikAutocomplete } from 'src/components/shared/form/formik/FormikAutocomplete'
import { debounce } from '@mui/material/utils'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { AddSingleMCPayment, FetchMCs, ValidateSingleMCPayment } from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { SinglePaymentInvalidReason } from 'src/constants/payment/enums'
import { WarningModal } from 'src/components/shared/WarningModal'
import { SearchMinor } from '@shopify/polaris-icons'
import { WarningContainer } from 'src/components/shared/WarningContainer'
import { Pagination } from 'src/interfaces/table'
import { MCStatus } from 'src/constants/mc/enums'
import { MC } from 'src/interfaces/mc'

interface Props {
  onSuccess(): void
}

const initialValues: AddSingleMCPaymentFormValues = {
  masterContractorExternalId: '',
  amount: '',
}

const AMOUNT_MASK: any = (rawValue: string) => {
  const numbers = rawValue
    .replace(/[^\d]+/g, '')
    .replace('$ ', '')
    .replace('.', '')

  const str = numbers.padStart(3, '0')

  const res = str.slice(0, -2) + '.' + str.slice(-2)

  return ['$', ' ', ...res.split('')].map((item) => (item.match(/\d/) ? /\d/ : item))
}

export const AddSingleMCPaymentForm = ({ onSuccess }: Props) => {
  const dispatch = useDispatch()

  const formikRef = useRef<FormikProps<AddSingleMCPaymentFormValues>>(null)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>('')
  const [selectedMC, setSelectedMC] = useState<MC | null>(null)
  const [mcs, setMCs] = useState<Array<MC>>([])
  const [isHasUnprocessedModalOpen, setIsHasUnprocessedModalOpen] = useState<boolean>(false)
  const [isHasAlreadyProcessedModalOpen, setIsHasAlreadyProcessedModalOpen] =
    useState<boolean>(false)

  const [amount, setAmount] = useState<number>(0)
  const [masterContractorExternalId, setMCId] = useState<string>('')

  const optionsList = useMemo(() => {
    const res: MC[] = []

    if (
      selectedMC &&
      !mcs.some((item) => item.masterContractorId === selectedMC.masterContractorId)
    ) {
      res.push(selectedMC)
    }

    res.push(...mcs)

    return res.map((item) => ({
      label: item.companyName,
      subLabel: item.masterContractorExternalId,
      value: item.masterContractorExternalId,
      mc: item,
    }))
  }, [selectedMC, mcs])

  const fetchMCs = useMemo(
    () =>
      debounce(async (searchQuery?: string) => {
        const params: any = {
          paymentsAvailableOnly: true,
          status: MCStatus.Activated,
        }

        if (searchQuery) {
          params.search = searchQuery
        }

        const payload: { data: Array<MC>; pagination: Pagination } = await createAsyncAction(
          dispatch,
          FetchMCs.request(params),
        )

        setMCs(payload.data)
      }, 300),
    [],
  )

  useEffect(() => {
    if (!inputValue) {
      setMCs([])

      return
    }

    fetchMCs(inputValue)
  }, [inputValue])

  const createMCPayment = useCallback(
    async ({
      amount,
      masterContractorExternalId,
    }: {
      masterContractorExternalId: string
      amount: number
    }) => {
      try {
        await createAsyncAction(
          dispatch,
          AddSingleMCPayment.request({
            amount,
            masterContractorExternalId,
          }),
        )

        showToast('Settlement is created!')
        onSuccess()
      } catch (err: any) {
        handleApiError(err)
      } finally {
        setIsLoading(false)
      }
    },
    [],
  )

  const createMCPaymentFromLocalState = useCallback(async () => {
    try {
      await createMCPayment({
        amount,
        masterContractorExternalId: masterContractorExternalId,
      })

      setAmount(0)
      setMCId('')
    } catch {
      //
    }
  }, [amount, masterContractorExternalId, createMCPayment])

  const handleFormSubmit = async (values: AddSingleMCPaymentFormValues) => {
    setIsLoading(true)

    const resAmount = +values.amount.replace('$ ', '').replace('.', '')

    try {
      const { isValid, reason }: { isValid: boolean; reason?: SinglePaymentInvalidReason } =
        await createAsyncAction(
          dispatch,
          ValidateSingleMCPayment.request({
            amount: resAmount,
            masterContractorExternalId: values.masterContractorExternalId,
          }),
        )

      if (isValid) {
        await createMCPayment({
          amount: resAmount,
          masterContractorExternalId: values.masterContractorExternalId,
        })

        return
      }

      if (!reason) {
        return
      }

      setAmount(resAmount)
      setMCId(values.masterContractorExternalId)

      if (reason === SinglePaymentInvalidReason.HasAlreadyProcessed) {
        setIsHasAlreadyProcessedModalOpen(true)

        return
      }

      if (reason === SinglePaymentInvalidReason.HasUnprocessed) {
        setIsHasUnprocessedModalOpen(true)

        return
      }
    } catch (err: any) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = useCallback((event: any, newInputValue: string) => {
    setInputValue(newInputValue)
  }, [])

  const closeHasAlreadyProcessedModal = useCallback(async () => {
    setIsHasAlreadyProcessedModalOpen(false)
  }, [])

  const closeHasUnprocessedModal = useCallback(async () => {
    setIsHasUnprocessedModalOpen(false)
  }, [])

  return (
    <Fragment>
      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={AddSingleMCPaymentSchema}
        innerRef={formikRef}
      >
        {({ handleSubmit, setFieldValue }) => (
          <div css={tw`w-full bg-white`}>
            <div css={tw`p-4`}>
              <FormikAutocomplete
                name="masterContractorExternalId"
                label="Select master contractor"
                placeholder="Start typing name"
                noOptionsText="No master contractors"
                options={optionsList}
                filterOptions={(x) => x}
                onInputChange={handleInputChange}
                onChange={(value) => {
                  setSelectedMC(
                    optionsList.find((item) => item.mc.masterContractorId === value)?.mc || null,
                  )
                  setFieldValue('masterContractorExternalId', value)
                }}
                startIcon={<SearchMinor width={20} />}
              />
              <div css={tw`w-full mt-4`}>
                <FormikTextField
                  name="amount"
                  label="Enter amount"
                  css={tw`w-full`}
                  mask={AMOUNT_MASK}
                  placeholder="$ 00.00"
                />
              </div>
              <WarningContainer
                title="Please ensure you enter the amount in the correct format: $0.00. Incorrect formats may cause errors in processing."
                subtitle="For example, $10.50 or $5.25."
                className="mt-4"
              />
            </div>
            <div
              css={tw`flex justify-end px-4 py-3 border-0 border-t border-solid border-[#EDEDED]`}
            >
              <Button
                className={tw`w-[146px]`}
                variant="contained"
                size="small"
                loading={isLoading}
                onClick={() => handleSubmit()}
              >
                Add settlement
              </Button>
            </div>
          </div>
        )}
      </Formik>
      <WarningModal
        isOpen={isHasAlreadyProcessedModalOpen}
        title="Add single settlement"
        submitLabel="Add settlement"
        text="This contractor already has a settlement processed recently, are you sure you want to create another settlement for them?"
        onSubmit={createMCPaymentFromLocalState}
        onClose={closeHasAlreadyProcessedModal}
      />
      <WarningModal
        isOpen={isHasUnprocessedModalOpen}
        title="Add single settlement"
        submitLabel="Add settlement"
        text="This contractor already has an unprocessed settlement created, are you sure you want to create another settlement for them?"
        onSubmit={createMCPaymentFromLocalState}
        onClose={closeHasUnprocessedModal}
      />
    </Fragment>
  )
}
